const EPSILON = 1e-10;

const toPoint = (p) => ({ x: p.lng, y: p.lat });
const toLatLng = (p) => ({ lat: p.y, lng: p.x });

const nearlySame = (a, b) => Math.abs(a.x - b.x) < EPSILON && Math.abs(a.y - b.y) < EPSILON;

function segmentIntersection(a, b, c, d) {
  const r = { x: b.x - a.x, y: b.y - a.y };
  const s = { x: d.x - c.x, y: d.y - c.y };
  const denom = r.x * s.y - r.y * s.x;
  if (Math.abs(denom) < EPSILON) return null;

  const cma = { x: c.x - a.x, y: c.y - a.y };
  const t = (cma.x * s.y - cma.y * s.x) / denom;
  const u = (cma.x * r.y - cma.y * r.x) / denom;
  if (t < -EPSILON || t > 1 + EPSILON || u < -EPSILON || u > 1 + EPSILON) return null;

  return {
    point: {
      x: a.x + t * r.x,
      y: a.y + t * r.y,
    },
    edgeT: Math.max(0, Math.min(1, t)),
    lineT: Math.max(0, Math.min(1, u)),
  };
}

function uniqueIntersections(intersections) {
  return intersections.reduce((acc, intersection) => {
    if (acc.some(existing => nearlySame(existing.point, intersection.point))) return acc;
    return [...acc, intersection];
  }, []);
}

function cleanPath(path) {
  const clean = [];
  path.forEach((p) => {
    const prev = clean[clean.length - 1];
    if (!prev || Math.abs(prev.lat - p.lat) > EPSILON || Math.abs(prev.lng - p.lng) > EPSILON) {
      clean.push(p);
    }
  });
  if (clean.length > 1) {
    const first = clean[0];
    const last = clean[clean.length - 1];
    if (Math.abs(first.lat - last.lat) < EPSILON && Math.abs(first.lng - last.lng) < EPSILON) {
      clean.pop();
    }
  }
  return clean;
}

function extendLineAcrossPolygon(polygon, start, end) {
  const bounds = polygon.reduce((acc, p) => ({
    minX: Math.min(acc.minX, p.lng),
    maxX: Math.max(acc.maxX, p.lng),
    minY: Math.min(acc.minY, p.lat),
    maxY: Math.max(acc.maxY, p.lat),
  }), {
    minX: Infinity,
    maxX: -Infinity,
    minY: Infinity,
    maxY: -Infinity,
  });
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const length = Math.sqrt(dx * dx + dy * dy);
  if (length < EPSILON) return [start, end];
  const span = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, EPSILON);
  const scale = (span * 4) / length;
  return [
    { x: start.x - dx * scale, y: start.y - dy * scale },
    { x: end.x + dx * scale, y: end.y + dy * scale },
  ];
}

export function splitPolygonByLine(paths, splitLinePaths) {
  if (!Array.isArray(paths) || paths.length < 3) {
    return { success: false, error: 'Select a polygon area to split.' };
  }
  if (!Array.isArray(splitLinePaths) || splitLinePaths.length < 2) {
    return { success: false, error: 'Draw a split line through the area.' };
  }

  const polygon = cleanPath(paths);
  const [lineStart, lineEnd] = extendLineAcrossPolygon(
    polygon,
    toPoint(splitLinePaths[0]),
    toPoint(splitLinePaths[splitLinePaths.length - 1])
  );
  const intersections = [];

  for (let i = 0; i < polygon.length; i += 1) {
    const nextIdx = (i + 1) % polygon.length;
    const hit = segmentIntersection(
      toPoint(polygon[i]),
      toPoint(polygon[nextIdx]),
      lineStart,
      lineEnd
    );
    if (hit) {
      intersections.push({
        ...hit,
        edgeIndex: i,
        nextIndex: nextIdx,
      });
    }
  }

  const unique = uniqueIntersections(intersections)
    .sort((a, b) => a.edgeIndex - b.edgeIndex || a.edgeT - b.edgeT);

  if (unique.length !== 2) {
    return {
      success: false,
      error: 'The split line must cross the area boundary exactly two times.',
    };
  }

  const [a, b] = unique;
  const intersectionA = toLatLng(a.point);
  const intersectionB = toLatLng(b.point);

  const pathOne = [intersectionA];
  let idx = a.nextIndex;
  while (idx !== b.nextIndex) {
    pathOne.push(polygon[idx]);
    idx = (idx + 1) % polygon.length;
  }
  pathOne.push(intersectionB);

  const pathTwo = [intersectionB];
  idx = b.nextIndex;
  while (idx !== a.nextIndex) {
    pathTwo.push(polygon[idx]);
    idx = (idx + 1) % polygon.length;
  }
  pathTwo.push(intersectionA);

  const cleanOne = cleanPath(pathOne);
  const cleanTwo = cleanPath(pathTwo);
  if (cleanOne.length < 3 || cleanTwo.length < 3) {
    return {
      success: false,
      error: 'The split line created an invalid area. Try drawing farther across the polygon.',
    };
  }

  return {
    success: true,
    paths: [cleanOne, cleanTwo],
    intersections: [intersectionA, intersectionB],
  };
}

export default splitPolygonByLine;
